import PageHeader from "components/Page/PageHeader";
import { Box, Flex } from "@chakra-ui/layout";
import { useState, useCallback, useMemo } from "react";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import useFiltroRelatorioComposicaoContas from "ui/components/Relatorio/FiltroRelatorioComposicaoContas/hooks/useFiltroRelatorioComposicaoContas";
import { useCurrentTenant } from "features/auth/tenant.store";
import ModalFiltrosRelatorioComposicaoContas from "ui/components/Relatorio/FiltroRelatorioComposicaoContas/ModalFiltrosRelatorioComposicaoContas";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import moment from "moment";
import { exportExcelRelatorioComposicaoContas } from "../util/helper";
import useRelatorioComposicaoContasPdf from "core/features/relatorios/conta-com-composicoes/hooks/useRelatorioComposicaoContasPdf";
import PDFRelatorioComposicaoReceitas from "../PDFRelatorio/index";
import RelatorioComposicaoContasContent from "../RelatorioComposicaoContent";
import useRelatorioComposicaoContasInfiniteScroll from "core/features/relatorios/conta-com-composicoes/hooks/useRelatorioComposicaoContasInfiniteScroll";
import { RelatorioComposicaoContasItem } from "core/features/relatorios/conta-com-composicoes/typings";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import { trackEvent } from "infra/helpers/event";
import {
  RelatorioReceitaComposicaoEvents,
  RelatorioDespesaComposicaoEvents,
} from "core/features/relatorios/enums/events";
import {
  fieldsRelatorioContasComComposicaoColumns,
  RelatorioContasComComposicaoFields,
} from "core/features/relatorios/typings";
import ExportMenu from "components/ExportMenu";
import SelectTipoData from "components/Input/SelectTipoData";
import DateRangePicker from "components/Input/DateRangePicker";
import { OperacaoEnum } from "core/resources/enums/conta";
import SelectableButton from "components/Button/SelectableButton";
import colors from "resources/theme/colors";
import ModalSelectFieldsRelatorio from "ui/components/ModalSelectFieldsRelatorio/index";
import useResumoPorCategoria from "core/features/relatorios/conta-com-composicoes/hooks/useResumoPorCategoria";
import { useLoading } from "ui/components/GlobalLoading";
import ModalComposicaoConta from "components/ModalComposicaoConta";
import { roundTo } from "round-to";
import ButtonResumoPorCategoria from "../../../Button/ButtonResumoPorCategoria";

function ListagemComposicao({ operacao }: { operacao: OperacaoEnum }) {
  const { filtro, onChangeFiltro, onResetFiltro } =
    useFiltroRelatorioComposicaoContas(operacao);

  const [exportType, setExportType] = useState<"pdf" | "xlsx" | undefined>();

  const {
    data: dataComposicoes,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRelatorioComposicaoContasInfiniteScroll(filtro, operacao);

  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);
  const { tenant } = useCurrentTenant();

  const { isLoading: isLoadingPdf, mutate: handleRelatorioPdf } =
    useRelatorioComposicaoContasPdf(filtro, operacao);

  const [isOpenModalResumoCategoria, setIsOpenModalResumoCategoria] =
    useState(false);

  const {
    isLoading: isLoadingResumoPorCategoria,
    mutate: fetchResumoPorCategoria,
    data: resumoPorCategoria,
  } = useResumoPorCategoria(filtro, operacao);

  const totalResumo = roundTo(
    resumoPorCategoria?.reduce((acc, next) => (acc += next.valor ?? 0), 0) || 0,
    2
  );

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de ${operacao === OperacaoEnum.Despesa ? "Despesas" : "Receitas"
      } com Composição - ${moment().format("DD_MM_YYYY")}`,
    Component: PDFRelatorioComposicaoReceitas,
    loading: isLoadingPdf,
  });

  const _onExportPdf = useCallback(
    async (
      fields?: RelatorioContasComComposicaoFields[],
      assinaturas?: AssinaturasItem[]
    ) => {
      trackEvent(
        operacao === OperacaoEnum.Despesa
          ? RelatorioDespesaComposicaoEvents.ExportarPDF
          : RelatorioReceitaComposicaoEvents.ExportarPDF
      );
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          onExportPdf({
            data: { ...data, assinaturas },
            operacao,
            fields,
          }).then(() => setExportType(undefined));
        },
      });
    },
    [handleRelatorioPdf, onExportPdf, operacao]
  );

  const _onExportExcel = useCallback(
    async (fields?: RelatorioContasComComposicaoFields[]) => {
      trackEvent(
        operacao === OperacaoEnum.Despesa
          ? RelatorioDespesaComposicaoEvents.ExportarExcel
          : RelatorioReceitaComposicaoEvents.ExportarExcel
      );
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          exportExcelRelatorioComposicaoContas({
            data,
            operacao,
            fields,
          });
        },
      });
    },
    [handleRelatorioPdf, operacao]
  );

  const numeroFiltroSelecionados =
    (filtro.contasCategoriasIds?.length ? 1 : 0) +
    (filtro.dataInicio || filtro.dataFim ? 1 : 0) +
    (filtro.tipoData ? 1 : 0) +
    (filtro.unidadesIds?.length ? 1 : 0) +
    (filtro.contasBancariasIds?.length ? 1 : 0) +
    (filtro.centrosDeCustoIds?.length ? 1 : 0) +
    (filtro.usuarioOperadorIds?.length ? 1 : 0);

  const composicoes = useMemo(() => {
    return (
      dataComposicoes?.pages.reduce(
        (agg: RelatorioComposicaoContasItem[], item) =>
          agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [dataComposicoes]);

  const onOpenResumoPorCategoria = () => {
    fetchResumoPorCategoria(undefined, {
      onSuccess: () => {
        setIsOpenModalResumoCategoria(true);
      },
    });
  };

  useLoading(isLoadingResumoPorCategoria);

  return (
    <>
      <PageHeader
        justifyContent="space-between"
        title={`Relatório de ${operacao === OperacaoEnum.Despesa ? "despesas" : "receitas"
          } com composição`}
        midComponent={
          <Flex gap={4}>
            <Box>
              <SelectTipoData
                bg="white"
                style={{ border: "1px solid #ccc", borderRadius: 6 }}
                value={filtro.tipoData}
                onChange={(v) => onChangeFiltro({ tipoData: v })}
                operacao={operacao}
              />
            </Box>

            <Box>
              <DateRangePicker
                onChange={(dataInicio, dataFim) => {
                  onChangeFiltro({
                    dataInicio,
                    dataFim,
                  });
                }}
                value={{
                  dataInicio: filtro.dataInicio,
                  dataFim: filtro.dataFim,
                }}
              />
            </Box>
          </Flex>
        }
      />

      <ListingPageHeaderBar
        valueSearch={filtro.search}
        onSearch={(search) => onChangeFiltro({ search })}
        visibleFiltro
        badge
        badgeValue={numeroFiltroSelecionados}
        onFiltro={() => setIsOpenModalFiltro(true)}
        children={
          <>
            <Flex gridGap="16px" flexWrap="wrap" w="100%">
              <SelectableButton
                selected={filtro.pago === "1"}
                onClick={() =>
                  onChangeFiltro({
                    pago: "1",
                  })
                }
                onUnselect={() =>
                  onChangeFiltro({
                    pago: undefined,
                  })
                }
                rounded="full"
                selectedColor={colors.green500}
                fontSize="14px"
              >
                Em aberto
              </SelectableButton>
              <SelectableButton
                selected={filtro.pago === "0"}
                onClick={() =>
                  onChangeFiltro({
                    pago: "0",
                  })
                }
                onUnselect={() =>
                  onChangeFiltro({
                    pago: undefined,
                  })
                }
                rounded="full"
                selectedColor={colors.orange}
                fontSize="14px"
              >
                Liquidadas
              </SelectableButton>

              {composicoes?.length ? (
                <Flex marginLeft="auto" gap={4}>
                  <ButtonResumoPorCategoria
                    onClick={onOpenResumoPorCategoria}
                  />

                  <ExportMenu
                    onExportPdf={() => setExportType("pdf")}
                    onExportarXlsx={() => setExportType("xlsx")}
                  />
                </Flex>
              ) : null}
            </Flex>
          </>
        }
      />

      <RelatorioComposicaoContasContent
        data={composicoes}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        fetchNextPage={fetchNextPage}
        hasNextPage={!!hasNextPage}
        operacao={operacao}
        valorTotal={dataComposicoes?.pages?.[0]?.valorTotal}
      />

      <ModalFiltrosRelatorioComposicaoContas
        tenantId={tenant.id}
        filtro={filtro}
        onChangeFiltro={onChangeFiltro}
        onReset={onResetFiltro}
        visible={isOpenModalFiltro}
        onClose={() => setIsOpenModalFiltro(false)}
        operacao={operacao}
      />

      <ModalSelectFieldsRelatorio
        visible={!!exportType}
        includeAssinaturas={exportType === "pdf"}
        onClose={() => setExportType(undefined)}
        onSubmit={({ fields, assinaturas }) => {
          exportType === "pdf"
            ? _onExportPdf(fields, assinaturas)
            : _onExportExcel(fields);

          setExportType(undefined);
        }}
        fields={fieldsRelatorioContasComComposicaoColumns.filter((field) => filtro.usuarioOperadorIds?.length ? true : field.value !== 'pessoaOperadorAcordo')}
      />

      {resumoPorCategoria ? (
        <ModalComposicaoConta
          tableTitle="Categoria"
          data={{
            composicoes: resumoPorCategoria?.map((item) => ({
              valor: item.valor,
              categoriaFinanceiraId: item.id,
              categoriaFinanceira: {
                breadcrumb: item.breadcrumb,
                operacao: item.operacao ?? operacao,
                nome: item.nome,
                id: item.id,
              },
            })),
            total: totalResumo,
          }}
          onClose={() => setIsOpenModalResumoCategoria(false)}
          isOpen={isOpenModalResumoCategoria}
          title="Resumo por categoria"
        />
      ) : null}
    </>
  );
}

export default ListagemComposicao;
